import {Button, Grid} from '@mui/material';
import React, {useState, useEffect, useCallback} from 'react';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ScreenHeader} from '../../components/screenHeader';
import {DeleteOutline} from '@mui/icons-material';
import {
    ComplainAdminsComment,
    ComplainAttachments,
    ComplainDataForm,
    ComplainDuplicateList,
    ComplainHistory,
    ComplainMessages,
    ComplainStatusForm,
    ConsumerInfoForm,
    CompanyAdditionalInfoForm,
    CompanyInfoForm,
    ComplainLogsList
} from './components';
import {PrivateIconWithBg, PublicIconWithBg} from '../../assets/icons';
import {format} from '../../utils/timeZoneUtils';
import ComplaintService from '../../components/services/ComplaintService';
import {apiClient} from '../../providers/apiClient';
import {APP_URLS} from '../../app/config';
import useSectors from "../../hooks/useSectors";

const ComplaintEditScreenV2 = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {t} = useTranslation(['Pages', 'Buttons', 'Modals']);
    const {t: tGlobal} = useTranslation();
    const {appSectors} = useSectors();

    const [model, setModel] = useState(null);
    const [company, setCompany] = useState(null);
    const [consumer, setConsumer] = useState(null);
    const [complainTypes, setComplainTypes] = useState([]);
    const [duplicateComplains, setDuplicateComplains] = useState({items: [], hasPrevious: false, hasNext: false});
    const [logs, setLogs] = useState({items: [], hasPrevious: false, hasNext: false});
    const [isStatusChangeRequested, setIsStatusChangeRequested] = useState(false);
    const [filter, setFilter] = useState({orderBy: 'createdAt', orderDirection: 'desc', pageNumber: 1});
    const [logsFilter, setLogsFilter] = useState({orderBy: 'createdAt', orderDirection: 'desc', pageNumber: 1});
    const [selectedItems, setSelectedItems] = useState([]);


    const refreshAllData = useCallback(async (newFilter, newLogsFilter) => {
        const currentFilter = newFilter || filter;
        const currentLogsFilter = newLogsFilter || logsFilter;

        try {
            // Step 1: Get main complain model
            const complainRes = await apiClient.get(APP_URLS.COMPLAINT_GET_ADMIN.replace('{id}', id));
            const complainData = complainRes.data;

            setModel(complainData);

            // Step 2: Get related data that depends on the complain model
            if (complainData) {
                // Fetch company
                if (complainData.company?.id) {
                    const companyRes = await apiClient.get(APP_URLS.COMPANY_GET.replace('{id}', complainData.company.id));
                    const companyData = companyRes.data;
                    setCompany(companyData);

                    // Step 3: Get data that depends on the company
                    if (companyData?.sectorId) {
                        const typesRes = await apiClient.get(APP_URLS.COMPLAIN_TYPES.replace('{id}', companyData.sectorId));
                        // The API returns {id, name}, but the component expects {value, text}
                        const mappedTypes = typesRes.data.map(t => ({value: t.id, text: t.name}));
                        setComplainTypes(mappedTypes);
                    }
                }
                // Fetch consumer
                if (complainData.reporter?.id) {
                    const consumerRes = await apiClient.get(APP_URLS.CONSUMER_GET.replace('{id}', complainData.reporter.id));
                    setConsumer(consumerRes.data);
                }
            }

            // Step 4: Fetch lists
            const params = {parentId: id, ...currentFilter};
            const res = await apiClient.get(APP_URLS.COMPLAINS_LIST, {params});
            setDuplicateComplains(res.data);

            const logParams = {complaintId: id, ...currentLogsFilter};
            const logRes = await apiClient.get(APP_URLS.LOGS_LIST, {params: logParams});
            setLogs(logRes.data);

        } catch (e) {
            console.error("Error refreshing data:", e);
        }
    }, [id, filter, logsFilter]);

    useEffect(() => {
        refreshAllData();
    }, [id]);


    const handleModelFieldChange = (field, value) => {
        setModel(prev => ({...prev, [field]: value}));
    };

    const handleCompanyFieldChange = (field, value) => {
        setCompany(prev => ({...prev, [field]: value}));
    };

    const handleFieldSave = async (field, value, isStatus) => {
        try {
            await apiClient.put(APP_URLS.COMPLAIN_UPDATE.replace('{id}', id), {
                fieldName: field,
                value: String(value),
                isStatusEdit: !!isStatus,
            });
            if (isStatus) {
                setIsStatusChangeRequested(false);
            }
            await refreshAllData();
        } catch (e) {
            console.error('Error saving field:', e);
        }
    };

    const handlePhotoRemove = async (file) => {
        try {
            await apiClient.post(APP_URLS.COMPLAIN_REMOVE_PHOTO.replace('{id}', id), file);
            await refreshAllData();
        } catch (e) {
            console.error('Error removing photo:', e);
        }
    };

    const handleSendMessage = async (message, attachments) => {
        const formData = new FormData();
        attachments.forEach(file => {
            formData.append('Attachments', file);
        });
        formData.append('Message', message);
        formData.append('Reporter', false);
        formData.append('Company', false);
        formData.append('Admin', true);

        try {
            await apiClient.post(APP_URLS.COMPLAIN_SEND_MESSAGE.replace('{id}', id), formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            });
            alert(tGlobal('ComplainDetailsScreen:Messages:messageSent'));
            await refreshAllData();
            return true;
        } catch (e) {
            alert(t('ComplainDetailsScreen:Messages:messageSentError'));
            return false;
        }
    };

    const handleMessageDelete = async (messageId) => {
        try {
            await apiClient.delete(`/api/Message/delete/${messageId}`);
            await refreshAllData();
        } catch (e) {
            console.error('Error deleting message', e);
            alert('Error deleting message.');
        }
    };

    const addAdminComment = async (field, value) => {
        await ComplaintService.addAdminComment(id, value);
        await refreshAllData();
    };

    const handlePageChange = (setter, newPage) => {
        const newFilter = {...filter, pageNumber: newPage};
        setter(newFilter);
        refreshAllData(newFilter, logsFilter);
    };

    const handleSort = (setter, property) => {
        const isAsc = filter.orderBy === property && filter.orderDirection === 'asc';
        const newFilter = {...filter, orderBy: property, orderDirection: isAsc ? 'desc' : 'asc'};
        setter(newFilter);
        refreshAllData(newFilter, logsFilter);
    };

    const handleRowCheck = (item, isChecked) => {
        if (!isChecked) {
            setSelectedItems(prev => [...prev, item]);
        } else {
            setSelectedItems(prev => prev.filter(i => i.id !== item.id));
        }
    };

    const handleSelectAll = (items) => {
        if (selectedItems.length === items.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(items);
        }
    };

    const handleSendAgainToCompany = async () => {
        try {
            await apiClient.get(APP_URLS.COMPLAIN_SEND_AGAIN.replace('{id}', id));
            alert(t('Modals:ComplaintResendSuccess'));
        } catch (e) {
            alert(t('Modals:ComplaintResendError'));
        }
    };

    const handleChangeVisibilityIsBlocked = async () => {
        await apiClient.get(APP_URLS.COMPLAIN_CHANGE_VISIBILITY_IS_BLOCKED.replace('{id}', id));
        await refreshAllData();
    };

    const handleExportPdf = async () => {
        try {
            const response = await apiClient.get(APP_URLS.COMPLAINT_EXPORT_PDF.replace('{id}', id), {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `complaint-${model.number}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Error exporting PDF:', e);
        }
    };

    const handleDelete = () => {
        if (window.confirm(`${t('Modals:ComplaintDeleteMessage')} ${model.number}?`)) {
            apiClient.delete(APP_URLS.COMPLAIN_REMOVE.replace('{id}', model.id)).then(() => {
                alert(t('Modals:ComplaintDeleteSuccess'));
                navigate('/complaints');
            }).catch(() => {
                alert(t('Modals:ComplaintDeleteError'));
            });
        }
    };

    const handleCloseComplaint = () => {
        const isSuccess = window.confirm('Has the problem been resolved?');
        apiClient.post(APP_URLS.COMPLAIN_CLOSE.replace('{id}', model.id), {isSuccess})
            .then(refreshAllData);
    };

    if (!model) return <div>Loading...</div>;

    const before = new URLSearchParams(location?.search).get('before');
    const beforeUrl = before ? decodeURIComponent(before) : null;

    return (
        <div className="fk-screen">
            <ScreenHeader
                withBackButton
                withSeparator
                title="complainHeader"
                titleReplacement={['#COMPLAIN_NUMBER#', model.number]}
                subtitle={format(model.createdAt)}
                before={beforeUrl}
                navigateBackRoute={"/complaints"}
                additionalButtons={
                    <div className="screen-header-buttons">
                        <div className={`fk-change-complain-visibility ${model.isPublic ? '--public' : '--private'}`}>
                            <img src={model.isPublic ? PublicIconWithBg : PrivateIconWithBg} alt=""/>
                            {model.isPublic ? tGlobal('ComplainDetailsScreen:DataForm:public') : tGlobal('ComplainDetailsScreen:DataForm:notPublic')}
                            <Button className="green"
                                    onClick={async () => await handleFieldSave('IsPublic', !model.isPublic)}>[{t('Buttons:change')}]</Button>
                        </div>
                        <div className="right-side">
                             <Button style={{backgroundColor: '#005A99', color: 'white', marginRight: '18px', fontSize: '14px', fontWeight: '500', padding: '0 12px 0 12px'}} onClick={handleExportPdf}>Export to PDF</Button>
                            <div className="button-with-label">
                                <label>{model.changeVisibilityBlocked ? t('Buttons:visibilityChangeIsBlocked') : t('Buttons:visibilityChangeIsAvailable')}</label>
                                <Button className="green"
                                        onClick={handleChangeVisibilityIsBlocked}>[{t('Buttons:change')}]</Button>
                            </div>
                            <Button className="green"
                                    onClick={handleSendAgainToCompany}>{t('Buttons:sendAgainToCompany')}</Button>
                            <Button className="green"
                                    onClick={() => setIsStatusChangeRequested(true)}>{t('Buttons:changeComplainStatus')}</Button>
                            {!model?.isResolved &&
                                <Button className="green"
                                        onClick={handleCloseComplaint}>{t('Buttons:closeComplain')}</Button>
                            }
                            <Button className="red" onClick={handleDelete}><DeleteOutline/> {t('Buttons:delete')}
                            </Button>
                        </div>
                    </div>
                }
            />
            <Grid container spacing={4}>
                <Grid item md={6}>
                    <ComplainStatusForm model={model} onUpdate={handleFieldSave}
                                        isEditRequested={isStatusChangeRequested}/>
                    <ComplainDataForm model={model} complainTypes={complainTypes} onUpdate={handleFieldSave}
                                      onFieldChange={handleModelFieldChange}/>
                    <ComplainAttachments id={model.id} attachments={model.photos} onUpdate={refreshAllData}
                                         onPhotoRemove={handlePhotoRemove}/>
                </Grid>
                <Grid item md={6}>
                    <ConsumerInfoForm model={consumer}/>
                    <CompanyInfoForm model={company} sectors={appSectors} onFieldChange={handleCompanyFieldChange}/>
                    <CompanyAdditionalInfoForm model={company} onFieldChange={handleCompanyFieldChange}/>
                </Grid>
            </Grid>
            <ComplainDuplicateList
                data={duplicateComplains.items}
                parentId={model.id}
                selectedItems={selectedItems}
                filter={filter}
                hasPrevious={duplicateComplains.hasPrevious}
                hasNext={duplicateComplains.hasNext}
                onRequestSort={(e, prop) => handleSort(setFilter, prop)}
                onRowCheck={handleRowCheck}
                onSelectAll={() => handleSelectAll(duplicateComplains.items)}
                onPreviousPage={() => handlePageChange(setFilter, filter.pageNumber - 1)}
                onNextPage={() => handlePageChange(setFilter, filter.pageNumber + 1)}
            />
            <ComplainMessages model={model} onUpdate={refreshAllData} onSendMessage={handleSendMessage}
                              onMessageDelete={handleMessageDelete}/>
            <ComplainAdminsComment model={model} onUpdate={addAdminComment} onFieldChange={handleModelFieldChange}/>
            <ComplainHistory model={model}/>
            <ComplainLogsList
                data={logs.items}
                filter={logsFilter}
                hasPrevious={logs.hasPrevious}
                hasNext={logs.hasNext}
                onDetails
                onRequestSort={(e, prop) => handleSort(setLogsFilter, prop)}
                onPreviousPage={() => handlePageChange(setLogsFilter, logsFilter.pageNumber - 1)}
                onNextPage={() => handlePageChange(setLogsFilter, logsFilter.pageNumber + 1)}
            />
        </div>
    );
};

export default ComplaintEditScreenV2;
